// src/components/AdminProfileEditor.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Breadcrumbs,
  Link
} from '@mui/material';
import { ArrowBack, Person, Edit } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ProfilePage from '../ProfilePage'; // Import your existing ProfilePage component
import adminService from './AdminService';

const AdminProfileEditor = () => {
  const { userId } = useParams(); // Get userId from URL parameter
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Fetch the user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use your admin service to get the user's details
        const user = await adminService.getUserById(userId);
        setUserData(user);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    } else {
      setError('No user ID provided');
      setLoading(false);
    }
  }, [userId]);

  // Handle profile update success
  const handleProfileUpdateSuccess = () => {
    setUpdateSuccess(true);
  };

  // Return to admin dashboard
  const handleReturnToDashboard = () => {
    navigate('/profiles/admin');
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Admin header section */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }} elevation={1}>
        <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/profiles/admin');
            }}
          >
            Admin Dashboard
          </Link>
          <Typography color="text.primary">Edit User Profile</Typography>
        </Breadcrumbs>

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Edit sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" component="h1">
              Admin Mode: Edit User Profile
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleReturnToDashboard}
            sx={{ mt: { xs: 2, sm: 0 } }}
          >
            Return to Dashboard
          </Button>
        </Box>
      </Paper>

      {/* Loading, error and success states */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleReturnToDashboard}>
              Back to Dashboard
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {updateSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleReturnToDashboard}>
              Back to Dashboard
            </Button>
          }
        >
          User profile updated successfully
        </Alert>
      )}

      {/* User info summary */}
      {userData && !loading && (
        <Box sx={{ mb: 3 }}>
          <Paper sx={{ p: 2 }} elevation={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Person sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">
                Editing: {userData.name || userData.email}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              User ID: {userData.id} | Email: {userData.email} | Role: {userData.role || 'user'}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Render the ProfilePage component with admin props */}
      {userData && !loading && (
        <ProfilePage
          adminMode={true}
          userData={userData}
          userId={userId}
          onUpdateSuccess={handleProfileUpdateSuccess}
        />
      )}
    </Box>
  );
};

export default AdminProfileEditor;